import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, StickyNote, Loader2, History, RotateCcw, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { JobPaymentControls } from "@/components/JobPaymentControls";
import { AccessManager } from "@/components/AccessManager";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { eur, fmtDate } from "@/lib/format";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toReopen, setToReopen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", amount: "", date: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/clients/${id}`);
      setClient(res.data);
    } catch (e) {
      toast.error("Cliente no encontrado");
      navigate("/clientes");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const patchHistory = (jobId, fields) => {
    setClient((prev) => prev ? {
      ...prev,
      history: prev.history.map((h) => h.id === jobId ? { ...h, ...fields } : h),
    } : prev);
  };

  const reopen = async () => {
    const job = toReopen;
    setToReopen(null);
    try {
      await api.post(`/jobs/${job.id}/reopen`);
      toast.success("Trabajo devuelto a pendientes");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    }
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({ title: h.title, amount: h.amount != null ? String(h.amount) : "",
      date: (h.completed_date || h.date || "").slice(0, 10) });
  };

  const saveEdit = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      await api.put(`/jobs/${editing.id}`, {
        title: form.title, client_id: id, category: editing.category || "",
        amount: parseFloat(form.amount) || 0, date: form.date || null,
      });
      toast.success("Trabajo actualizado");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !client) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <button onClick={() => navigate("/clientes")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Volver a clientes
      </button>

      <div className="flex items-start justify-between gap-4">
        <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="client-name">{client.name}</h1>
        <div className="rounded-md border border-border bg-card px-4 py-2 text-right shrink-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Total facturado</p>
          <p className="font-display text-2xl font-bold text-primary" data-testid="client-total">{eur(client.history_total || 0)}</p>
        </div>
      </div>

      {/* Contact card */}
      <div className="rounded-md border border-border bg-card p-5 grid gap-3 sm:grid-cols-2">
        {client.phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-4 w-4 text-primary" /> {client.phone}</div>}
        {client.email && <div className="flex items-center gap-2 text-sm"><Mail className="h-4 w-4 text-primary" /> {client.email}</div>}
        {client.address && <div className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-primary" /> {client.address}</div>}
        {client.notes && <div className="flex items-start gap-2 text-sm sm:col-span-2"><StickyNote className="h-4 w-4 text-primary mt-0.5" /> <span className="text-muted-foreground">{client.notes}</span></div>}
      </div>

      {/* History */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Historial de trabajos</h2>
        </div>
        {client.history.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-muted-foreground">
            Aún no hay trabajos realizados para este cliente.
          </div>
        ) : (
          <div className="space-y-2" data-testid="client-history">
            {client.history.map((h) => {
              return (
                <div key={h.id} data-testid={`history-item-${h.id}`}
                  className="rounded-md border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{h.title}</p>
                      <div className="flex flex-wrap gap-x-3 text-sm text-muted-foreground mt-0.5">
                        <span>{fmtDate(h.completed_date)}</span>
                        {h.category && <span className="text-primary">· {h.category}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-display font-bold" data-testid={`history-amount-${h.id}`}>{eur(h.amount)}</span>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(h)} data-testid={`edit-history-${h.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setToReopen(h)}
                        data-testid={`reopen-history-${h.id}`} title="Devolver a pendientes">
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-border">
                    <JobPaymentControls job={h} onChange={(fields) => patchHistory(h.id, fields)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="pt-2">
        <AccessManager scope="client" clientId={id} />
      </section>

      {/* Edit history job dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-history-dialog">
          <DialogHeader><DialogTitle>Editar trabajo realizado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Título / descripción</Label>
              <Textarea value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="edit-history-title" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Dinero cobrado (€)</Label>
                <Input type="number" inputMode="decimal" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="edit-history-amount" />
              </div>
              <div>
                <Label>Fecha</Label>
                <Input type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="edit-history-date" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="save-edit-history">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toReopen} onOpenChange={(o) => !o && setToReopen(null)}>
        <AlertDialogContent data-testid="reopen-history-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Devolver a pendientes?</AlertDialogTitle>
            <AlertDialogDescription>
              El trabajo "{toReopen?.title}" volverá a la lista de trabajos pendientes y saldrá del historial hasta que lo marques de nuevo como realizado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={reopen} data-testid="confirm-reopen-history">Devolver a pendientes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
